import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import {
  ShieldCheck, Plus, Pencil, Trash2, LayoutGrid, Table2, PieChart, ChevronRight,
  AlertTriangle, RotateCcw, Upload, Download, FileText, FileSpreadsheet, Paperclip, XCircle,
  ClipboardList, PoundSterling,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { ASSET_TYPES, TYPE_MAP, COLUMNS, COL_MAP, daysLabel, CLAIM_MAP, CLAIM_STATUSES } from "../lib/assets";
import { Pager, usePaged } from "../components/Pager";
import { fmtDateGB } from "../lib/format";
import AssetModal from "../components/AssetModal";
import AssignSiteButton from "../components/AssignSiteButton";

const fmtGbp = (v) => v == null ? "—" : `£${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const TypeIcon = ({ type, size = 15, ...p }) => {
  const Icon = TYPE_MAP[type]?.icon || ShieldCheck;
  return <Icon size={size} {...p} />;
};

/* ── Warranty Kanban board ───────────────────────────────────────────────── */
function Board({ assets, onReclass, onEdit }) {
  const [dragId, setDragId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const grouped = COLUMNS.reduce((acc, c) => {
    acc[c.key] = assets.filter((a) => a.warranty_status === c.key);
    return acc;
  }, {});

  const drop = (colKey) => {
    if (dragId) {
      const a = assets.find((x) => x.id === dragId);
      if (a && a.warranty_status !== colKey) onReclass(a, colKey);
    }
    setDragId(null); setOverCol(null);
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4" data-testid="asset-board">
      {COLUMNS.map((c) => {
        const items = grouped[c.key] || [];
        const qty = items.reduce((a, x) => a + (Number(x.quantity) || 1), 0);
        return (
          <div key={c.key}
            onDragOver={(e) => { e.preventDefault(); setOverCol(c.key); }}
            onDragLeave={() => setOverCol((o) => (o === c.key ? null : o))}
            onDrop={() => drop(c.key)}
            className={`rounded-lg border transition-colors ${overCol === c.key ? "ring-2 ring-offset-1" : ""}`}
            style={{ borderColor: c.accent + "55", background: c.soft, ...(overCol === c.key ? { "--tw-ring-color": c.accent } : {}) }}
            data-testid={`asset-col-${c.key}`}>
            <div className="px-3 py-2.5 flex items-center justify-between border-b" style={{ borderColor: c.accent + "33" }}>
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full" style={{ background: c.accent }} />
                <span className="text-[11px] uppercase tracking-[0.12em] font-bold" style={{ color: c.accent }}>{c.label}</span>
              </div>
              <span className="text-[10px] font-mono muted">{items.length} · {qty} units</span>
            </div>
            <div className="p-2.5 space-y-2 min-h-[120px]">
              {items.length === 0 && <div className="text-[11px] muted text-center py-6">Drop assets here</div>}
              {items.map((a) => (
                <div key={a.id} draggable
                  onDragStart={(e) => { setDragId(a.id); e.dataTransfer.effectAllowed = "move"; try { e.dataTransfer.setData("text/plain", a.id); } catch {} }}
                  onDragEnd={() => { setDragId(null); setOverCol(null); }}
                  onClick={() => onEdit(a)}
                  className={`bg-white rounded border border-[var(--line)] p-2.5 cursor-grab active:cursor-grabbing hover:shadow-sm transition-shadow ${dragId === a.id ? "opacity-40" : ""}`}
                  data-testid={`asset-card-${a.id}`}>
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center w-6 h-6 rounded" style={{ background: c.soft, color: c.accent }}>
                      <TypeIcon type={a.asset_type} size={13} />
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="text-[12px] font-semibold truncate">{a.manufacturer || TYPE_MAP[a.asset_type]?.label || "Asset"} {a.model ? `· ${a.model}` : ""}</div>
                      <div className="text-[10px] muted font-mono truncate">{a.serial_number || "no serial"}{Number(a.quantity) > 1 ? ` ×${a.quantity}` : ""}</div>
                    </div>
                    {a.warranty_doc_id && <FileText size={12} className="text-[var(--ink-2)] shrink-0" title="Warranty certificate attached" />}
                    {a.is_override && (
                      <button title="Revert to auto status"
                        onClick={(e) => { e.stopPropagation(); onReclass(a, null); }}
                        className="text-[var(--ink-2)] hover:text-[var(--accent)]" data-testid={`asset-revert-${a.id}`}>
                        <RotateCcw size={12} />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center justify-between mt-1.5">
                    <span className="text-[10px] muted truncate">{a.project_name || "Unassigned"}</span>
                    <span className="text-[10px] font-semibold" style={{ color: c.accent }}>{daysLabel(a.days_remaining)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Register table ──────────────────────────────────────────────────────── */
function Register({ assets, onEdit, onDelete, selected, onToggle, onToggleAll, projects, onAssign }) {
  const { rows, pagerProps } = usePaged(assets, "eneprox.assets.perPage", assets.length);
  if (assets.length === 0) return <div className="muted text-sm p-6">No assets registered yet.</div>;
  const allSel = assets.length > 0 && assets.every((a) => selected.has(a.id));
  return (
    <div className="swiss-card-flat">
    <div className="overflow-x-auto">
      <table className="w-full text-sm" data-testid="asset-table">
        <thead>
          <tr className="text-left label border-b border-[var(--line)] bg-[var(--surface-2)]">
            <th className="py-3 px-3 w-8">
              <input type="checkbox" checked={allSel} onChange={(e) => onToggleAll(e.target.checked)}
                data-testid="asset-select-all" aria-label="Select all" />
            </th>
            <th className="py-3 px-4">Type</th>
            <th className="py-3 px-4">Manufacturer / Model</th>
            <th className="py-3 px-4">Serial</th>
            <th className="py-3 px-4 text-right">Qty</th>
            <th className="py-3 px-4">Site</th>
            <th className="py-3 px-4">Warranty end</th>
            <th className="py-3 px-4">Status</th>
            <th className="py-3 px-4 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => {
            const c = COL_MAP[a.warranty_status];
            return (
              <tr key={a.id} className={`border-b border-[var(--line)] hover:bg-[var(--bg-2)] ${selected.has(a.id) ? "bg-[var(--accent-soft,#DDF3F1)]" : ""}`} data-testid={`asset-tr-${a.id}`}>
                <td className="py-2 px-3">
                  <input type="checkbox" checked={selected.has(a.id)} onChange={() => onToggle(a.id)}
                    data-testid={`asset-select-${a.id}`} aria-label="Select asset" />
                </td>
                <td className="py-2 px-4"><span className="inline-flex items-center gap-1.5"><TypeIcon type={a.asset_type} size={14} /> {TYPE_MAP[a.asset_type]?.label || a.asset_type}</span></td>
                <td className="py-2 px-4 font-medium">{a.manufacturer || "—"}{a.model ? <span className="muted font-normal"> · {a.model}</span> : null}</td>
                <td className="py-2 px-4 font-mono text-xs">{a.serial_number || "—"}{a.warranty_doc_id ? <FileText size={11} className="inline ml-1.5 text-[var(--ink-2)]" /> : null}</td>
                <td className="py-2 px-4 text-right font-mono">{a.quantity}</td>
                <td className="py-2 px-4">{a.project_name || <span className="muted">Unassigned</span>}</td>
                <td className="py-2 px-4 font-mono text-xs">{fmtDateGB(a.warranty_end_date)}</td>
                <td className="py-2 px-4">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full inline-flex items-center gap-1"
                    style={{ background: c.soft, color: c.accent }}>
                    {c.label}{a.is_override ? " *" : ""}
                  </span>
                </td>
                <td className="py-2 px-4 text-right whitespace-nowrap">
                  <div className="inline-flex items-center gap-1.5">
                    <button onClick={() => onDelete(a)} className="btn-ghost !px-2.5 text-[#C0392B]" data-testid={`asset-delete-${a.id}`} title="Delete" aria-label="Delete asset"><Trash2 size={13} /></button>
                    <button onClick={() => onEdit(a)} className="btn-ghost !px-2.5" data-testid={`asset-edit-${a.id}`} title="Edit" aria-label="Edit asset"><Pencil size={13} /></button>
                    {a.project_id
                      ? <Link to={`/projects/${a.project_id}?tab=Assets`} className="btn-ghost" data-testid={`asset-open-${a.id}`}>Open <ChevronRight size={13} /></Link>
                      : <AssignSiteButton projects={projects} onAssign={(pid) => onAssign(a, pid)} testid={`asset-assign-${a.id}`} />}
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
      {assets.length > 0 && <Pager {...pagerProps} testid="assets-pager" />}
    </div>
  );
}

/* ── Report view ─────────────────────────────────────────────────────────── */
function Report({ report }) {
  if (!report) return <div className="muted text-sm p-6">Loading report…</div>;
  const typeRows = ASSET_TYPES.filter((t) => report.by_type[t.key]).map((t) => ({ ...t, ...report.by_type[t.key] }));
  const mfrRows = Object.entries(report.by_manufacturer).sort((a, b) => b[1].count - a[1].count);
  return (
    <div className="space-y-6" data-testid="asset-report">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {COLUMNS.map((c) => {
          const s = report.by_status[c.key] || { count: 0, qty: 0 };
          return (
            <div key={c.key} className="rounded-lg border p-4" style={{ borderColor: c.accent + "55", background: c.soft }} data-testid={`asset-stat-${c.key}`}>
              <div className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: c.accent }}>{c.label}</div>
              <div className="font-display text-3xl font-bold mt-1" style={{ color: c.accent }}>{s.count}</div>
              <div className="text-[11px] muted">{s.qty} units</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="swiss-card p-5">
          <div className="label mb-3">By asset type</div>
          <div className="space-y-2">
            {typeRows.length === 0 && <div className="muted text-sm">No assets yet.</div>}
            {typeRows.map((t) => (
              <div key={t.key} className="flex items-center gap-3" data-testid={`asset-type-row-${t.key}`}>
                <span className="inline-flex items-center gap-1.5 w-32 text-[13px] font-medium"><t.icon size={14} /> {t.label}</span>
                <div className="flex-1 h-2.5 rounded-full bg-[var(--line-soft,#EEF2F4)] overflow-hidden flex">
                  {COLUMNS.map((c) => {
                    const v = t.statuses[c.key].count;
                    const pct = t.count ? (v / t.count) * 100 : 0;
                    return pct > 0 ? <span key={c.key} style={{ width: `${pct}%`, background: c.accent }} /> : null;
                  })}
                </div>
                <span className="font-mono text-xs w-20 text-right">{t.count} · {t.qty}u</span>
              </div>
            ))}
          </div>
        </div>

        <div className="swiss-card p-5">
          <div className="label mb-3">By manufacturer</div>
          <div className="space-y-1.5">
            {mfrRows.length === 0 && <div className="muted text-sm">No assets yet.</div>}
            {mfrRows.map(([name, v]) => (
              <div key={name} className="flex items-center justify-between text-[13px] border-b border-[var(--line-soft,#EEF2F4)] py-1" data-testid={`asset-mfr-row-${name}`}>
                <span className="font-medium">{name}</span>
                <span className="font-mono text-xs muted">{v.count} lines · {v.qty} units</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="swiss-card p-5" data-testid="asset-expiring-list">
        <div className="label mb-3 flex items-center gap-2">
          <AlertTriangle size={13} className="text-[#B45309]" />
          Warranty reminders · expiring within {report.expiring_window_days} days or already expired
        </div>
        {report.expiring.length === 0 ? (
          <div className="muted text-sm">Nothing expiring. All tracked warranties are healthy. 🎉</div>
        ) : (
          <div className="space-y-1.5">
            {report.expiring.map((a) => {
              const c = COL_MAP[a.warranty_status];
              return (
                <div key={a.id} className="flex items-center justify-between text-[13px] py-1.5 border-b border-[var(--line-soft,#EEF2F4)]" data-testid={`asset-reminder-${a.id}`}>
                  <span className="inline-flex items-center gap-2 min-w-0">
                    <TypeIcon type={a.asset_type} size={14} style={{ color: c.accent }} />
                    <span className="font-medium truncate">{a.manufacturer || TYPE_MAP[a.asset_type]?.label} {a.model ? `· ${a.model}` : ""}</span>
                    <span className="muted truncate">· {a.project_name || "Unassigned"}</span>
                  </span>
                  <span className="font-semibold whitespace-nowrap" style={{ color: c.accent }}>
                    {fmtDateGB(a.warranty_end_date)} · {daysLabel(a.days_remaining)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Claims dashboard ────────────────────────────────────────────────────── */
function ClaimsDashboard({ data }) {
  if (!data) return <div className="muted text-sm p-6">Loading claims…</div>;
  const stats = [
    { l: "Total claims", v: data.total_claims, c: "var(--ink)" },
    { l: "Open / submitted", v: data.open_count, c: "#2563EB" },
    { l: "Approved", v: data.approved_count, c: "#15934F" },
    { l: "Resolved", v: data.resolved_count, c: "#0E7490" },
    { l: "£ recovered", v: fmtGbp(data.total_recovered_gbp), c: "#B45309" },
  ];
  return (
    <div className="space-y-6" data-testid="asset-claims-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {stats.map((s) => (
          <div key={s.l} className="swiss-card-flat p-4" data-testid={`claims-stat-${s.l}`}>
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">{s.l}</div>
            <div className="font-display text-2xl font-bold mt-1" style={{ color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {data.repeat_failures.length > 0 && (
        <div className="rounded-lg border border-[#F0C27B] bg-[#FEF3E2] p-4" data-testid="claims-repeat">
          <div className="label flex items-center gap-2 mb-2 text-[#92400E]"><AlertTriangle size={13} /> Repeat failures ({data.repeat_failures.length})</div>
          <div className="flex flex-wrap gap-2">
            {data.repeat_failures.map((r) => (
              <span key={r.asset_id} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-white border border-[#F0C27B] text-[#92400E]">
                {r.manufacturer || r.asset_type_label} · {r.serial_number || "no serial"} — {r.claim_count} claims
              </span>
            ))}
          </div>
        </div>
      )}

      {data.claims.length === 0 ? (
        <div className="muted text-sm p-6 swiss-card-flat">No warranty claims raised yet.</div>
      ) : (
        <div className="overflow-x-auto swiss-card-flat">
          <table className="w-full text-sm" data-testid="claims-table">
            <thead>
              <tr className="text-left label border-b border-[var(--line)] bg-[var(--surface-2)]">
                <th className="py-3 px-4">Asset</th>
                <th className="py-3 px-4">Site</th>
                <th className="py-3 px-4">Raised</th>
                <th className="py-3 px-4">Reference</th>
                <th className="py-3 px-4">Description</th>
                <th className="py-3 px-4 text-right">£ recovered</th>
                <th className="py-3 px-4">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.claims.map((c) => {
                const st = CLAIM_MAP[c.status] || CLAIM_STATUSES[0];
                return (
                  <tr key={c.id} className="border-b border-[var(--line)] hover:bg-[var(--bg-2)]" data-testid={`claims-row-${c.id}`}>
                    <td className="py-2 px-4 font-medium">{c.manufacturer || c.asset_type_label}{c.model ? <span className="muted font-normal"> · {c.model}</span> : ""}<div className="text-[10px] muted font-mono">{c.serial_number || "no serial"}</div></td>
                    <td className="py-2 px-4">{c.project_name || <span className="muted">Unassigned</span>}</td>
                    <td className="py-2 px-4 font-mono text-xs">{fmtDateGB(c.claim_date)}</td>
                    <td className="py-2 px-4 font-mono text-xs">{c.reference || "—"}</td>
                    <td className="py-2 px-4 max-w-[260px] truncate" title={c.description || ""}>{c.description || "—"}</td>
                    <td className="py-2 px-4 text-right font-mono">{c.cost_recovered_gbp ? fmtGbp(c.cost_recovered_gbp) : "—"}</td>
                    <td className="py-2 px-4"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: st.c + "22", color: st.c }}>{st.l}</span></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Warranty value report ───────────────────────────────────────────────── */
const COVERAGE_META = {
  protected: { label: "Protected", c: "#15934F" },
  partial: { label: "Partial cover", c: "#B45309" },
  exposed: { label: "Exposed", c: "#C0392B" },
  untracked: { label: "Untracked", c: "#64748B" },
};
function WarrantyValue({ data }) {
  if (!data) return <div className="muted text-sm p-6">Loading warranty value…</div>;
  const total = data.total_capex_gbp || 0;
  const seg = [
    { k: "under", label: "Under warranty", v: data.under_warranty_capex_gbp, c: "#15934F" },
    { k: "exposed", label: "Out-of-warranty exposure", v: data.exposed_capex_gbp, c: "#C0392B" },
    { k: "untracked", label: "Untracked", v: data.untracked_capex_gbp, c: "#64748B" },
  ];
  return (
    <div className="space-y-6" data-testid="asset-warranty-value">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="swiss-card-flat p-4" data-testid="wv-total">
          <div className="text-[10px] uppercase tracking-[0.12em] font-bold muted">Total CAPEX tracked</div>
          <div className="font-display text-2xl font-bold mt-1">{fmtGbp(total)}</div>
        </div>
        {seg.map((s) => (
          <div key={s.k} className="rounded-lg border p-4" style={{ borderColor: s.c + "55", background: s.c + "11" }} data-testid={`wv-${s.k}`}>
            <div className="text-[10px] uppercase tracking-[0.12em] font-bold" style={{ color: s.c }}>{s.label}</div>
            <div className="font-display text-2xl font-bold mt-1" style={{ color: s.c }}>{fmtGbp(s.v)}</div>
            <div className="text-[11px] muted">{total > 0 ? Math.round((s.v / total) * 100) : 0}% of CAPEX</div>
          </div>
        ))}
      </div>

      {total > 0 && (
        <div className="h-4 rounded-full overflow-hidden flex border border-[var(--line)]" data-testid="wv-bar">
          {seg.map((s) => s.v > 0 ? <span key={s.k} style={{ width: `${(s.v / total) * 100}%`, background: s.c }} title={`${s.label}: ${fmtGbp(s.v)}`} /> : null)}
        </div>
      )}

      {data.sites.length === 0 ? (
        <div className="muted text-sm p-6 swiss-card-flat">No sites with registered assets yet.</div>
      ) : (
        <div className="overflow-x-auto swiss-card-flat">
          <table className="w-full text-sm" data-testid="wv-table">
            <thead>
              <tr className="text-left label border-b border-[var(--line)] bg-[var(--surface-2)]">
                <th className="py-3 px-4">Site</th>
                <th className="py-3 px-4 text-right">CAPEX</th>
                <th className="py-3 px-4">Coverage</th>
                <th className="py-3 px-4 text-right">Assets</th>
                <th className="py-3 px-4 text-right">Covered</th>
                <th className="py-3 px-4 text-right">Expired</th>
                <th className="py-3 px-4 text-right">Untracked</th>
              </tr>
            </thead>
            <tbody>
              {data.sites.map((s) => {
                const m = COVERAGE_META[s.coverage] || COVERAGE_META.untracked;
                return (
                  <tr key={s.project_id} className="border-b border-[var(--line)] hover:bg-[var(--bg-2)]" data-testid={`wv-row-${s.project_id}`}>
                    <td className="py-2 px-4 font-medium">{s.project_name}</td>
                    <td className="py-2 px-4 text-right font-mono font-semibold">{fmtGbp(s.capex_gbp)}</td>
                    <td className="py-2 px-4"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ background: m.c + "22", color: m.c }}>{m.label}</span></td>
                    <td className="py-2 px-4 text-right font-mono">{s.asset_count}</td>
                    <td className="py-2 px-4 text-right font-mono text-[#15934F]">{s.covered_count}</td>
                    <td className="py-2 px-4 text-right font-mono text-[#C0392B]">{s.expired_count}</td>
                    <td className="py-2 px-4 text-right font-mono muted">{s.untracked_assets}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ── Page ────────────────────────────────────────────────────────────────── */
export default function Assets() {
  const [searchParams] = useSearchParams();
  const [view, setView] = useState(searchParams.get("view") || "board");
  const [assets, setAssets] = useState([]);
  const [report, setReport] = useState(null);
  const [claimsData, setClaimsData] = useState(null);
  const [valueData, setValueData] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const [selected, setSelected] = useState(new Set());
  const [exportOpen, setExportOpen] = useState(false);
  const bulkDocRef = useRef(null);
  const [bulkBusy, setBulkBusy] = useState(false);

  const assignSite = async (a, pid) => {
    try {
      await api.patch(`/assets/${a.id}`, { project_id: pid });
      toast.success(`${a.manufacturer || "Asset"} linked to ${projects.find((p) => p.id === pid)?.name || "site"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Assign failed"); throw e; }
  };
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, r] = await Promise.all([
        api.get(`/assets${typeFilter ? `?asset_type=${typeFilter}` : ""}`),
        api.get(`/assets/report`),
      ]);
      setAssets(a.data.assets || []);
      setReport(r.data);
      api.get(`/claims`).then(({ data }) => setClaimsData(data)).catch(() => {});
      api.get(`/assets/warranty-value`).then(({ data }) => setValueData(data)).catch(() => {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load assets");
    } finally {
      setLoading(false);
    }
  }, [typeFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get(`/projects`).then(({ data }) => setProjects((data || []).map((p) => ({ id: p.id, name: p.name }))))
      .catch(() => {});
  }, []);

  const reclass = async (a, colKey) => {
    const prev = assets;
    setAssets((list) => list.map((x) => x.id === a.id
      ? { ...x, warranty_status: colKey || x.warranty_status, is_override: !!colKey } : x));
    try {
      await api.post(`/assets/${a.id}/warranty-status`, { warranty_status_override: colKey });
      toast.success(colKey ? `Moved to ${COL_MAP[colKey].label}` : "Reverted to auto status");
      load();
    } catch (e) {
      setAssets(prev);
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete this ${TYPE_MAP[a.asset_type]?.label || "asset"}? This cannot be undone.`)) return;
    try {
      await api.delete(`/assets/${a.id}`);
      toast.success("Asset deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const downloadTemplate = () => {
    const csv = "Type,Manufacturer,Model,Serial,Quantity,Site,Install Date,Warranty Start,Warranty Years,Notes\n" +
      "Solar Panel,JinkoSolar,Tiger Neo 440W,SN-0001,120,Example Site Name,2024-03-01,2024-03-01,25,Roof A\n" +
      "Inverter,SolarEdge,SE100K,SN-INV-1,2,Example Site Name,2024-03-01,2024-03-01,12,\n";
    const dl = document.createElement("a");
    dl.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    dl.download = "asset_import_template.csv";
    document.body.appendChild(dl); dl.click();
    setTimeout(() => { URL.revokeObjectURL(dl.href); dl.remove(); }, 200);
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/assets/import-csv`, fd);
      let msg = `Imported ${data.imported} asset${data.imported === 1 ? "" : "s"}`;
      if (data.skipped) msg += ` · skipped ${data.skipped}`;
      toast.success(msg);
      if (data.unmatched_sites?.length) toast.warning(`Unmatched sites (left unassigned): ${data.unmatched_sites.join(", ")}`);
      if (data.errors?.length) toast.error(data.errors.slice(0, 3).join(" · "));
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = (checked) => setSelected(checked ? new Set(assets.map((a) => a.id)) : new Set());

  const exportRegister = async (fmt) => {
    setExportOpen(false);
    try {
      const { data } = await api.get(`/assets/export?format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `asset_register_${new Date().toISOString().slice(0, 10)}.${fmt === "xlsx" ? "xlsx" : "csv"}`;
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 200);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const bulkAttach = async (e) => {
    const file = e.target.files?.[0];
    if (!file || selected.size === 0) return;
    setBulkBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post(`/uploads`, fd);
      await api.post(`/assets/bulk-warranty-doc`, {
        asset_ids: Array.from(selected),
        warranty_doc_id: up.data.id,
        warranty_doc_name: up.data.original_filename || file.name,
      });
      toast.success(`Certificate attached to ${selected.size} asset${selected.size === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Bulk attach failed");
    } finally {
      setBulkBusy(false);
      if (bulkDocRef.current) bulkDocRef.current.value = "";
    }
  };

  const bulkRemoveDoc = async () => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      await api.post(`/assets/bulk-warranty-doc`, { asset_ids: Array.from(selected), warranty_doc_id: null, warranty_doc_name: null });
      toast.success(`Certificate removed from ${selected.size} asset${selected.size === 1 ? "" : "s"}`);
      setSelected(new Set());
      load();
    } catch (err) {
      toast.error("Bulk remove failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const expiringCount = (report?.by_status?.expiring_soon?.count || 0) + (report?.by_status?.expired?.count || 0);

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="assets-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <div className="label mb-2">Execution</div>
          <h1 className="font-display text-4xl font-bold">Asset &amp; warranty register</h1>
          <p className="muted mt-2 text-sm max-w-2xl">
            Register every panel, optimizer, inverter, meter, gateway and battery with its serial number and
            warranty term. Track warranty health on the board and act on reminders before cover lapses.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} className="btn-ghost text-xs" data-testid="asset-template-btn">
            <Download size={13} /> Template
          </button>
          <button onClick={() => fileRef.current?.click()} disabled={importing} className="btn-ghost text-xs" data-testid="asset-import-btn">
            <Upload size={13} /> {importing ? "Importing…" : "Import CSV / Excel"}
          </button>
          <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" className="hidden" onChange={importCsv} data-testid="asset-import-input" />
          <div className="relative">
            <button onClick={() => setExportOpen((o) => !o)} className="btn-ghost text-xs" data-testid="asset-export-btn">
              <FileSpreadsheet size={13} /> Export
            </button>
            {exportOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setExportOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 bg-white border border-[var(--line)] rounded-lg shadow-lg py-1 min-w-[140px]" data-testid="asset-export-menu">
                  <button onClick={() => exportRegister("csv")} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-2)]" data-testid="asset-export-csv">Export as CSV</button>
                  <button onClick={() => exportRegister("xlsx")} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-[var(--bg-2)]" data-testid="asset-export-xlsx">Export as Excel</button>
                </div>
              </>
            )}
          </div>
          <button onClick={() => { setEditing(null); setModalOpen(true); }} className="btn-primary" data-testid="asset-add-btn">
            <Plus size={15} /> Register asset
          </button>
        </div>
      </div>

      {expiringCount > 0 && (
        <div className="mb-5 flex items-center gap-2 text-sm px-4 py-2.5 rounded-lg border border-[#F0C27B] bg-[#FEF3E2] text-[#92400E]" data-testid="asset-reminder-banner">
          <AlertTriangle size={15} />
          <span><b>{expiringCount}</b> warrant{expiringCount === 1 ? "y" : "ies"} expiring soon or already expired — see the Report tab for the full list.</span>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
        <div className="inline-flex rounded border border-[var(--line)] overflow-hidden text-xs font-semibold" data-testid="asset-view-toggle">
          {[
            { k: "board", l: "Board", icon: LayoutGrid },
            { k: "register", l: "Register", icon: Table2 },
            { k: "report", l: "Report", icon: PieChart },
            { k: "claims", l: "Claims", icon: ClipboardList },
            { k: "value", l: "Warranty Value", icon: PoundSterling },
          ].map((v) => (
            <button key={v.k} onClick={() => setView(v.k)}
              className={`px-3.5 py-1.5 inline-flex items-center gap-1.5 transition-colors ${view === v.k ? "bg-[var(--ink)] text-white" : "bg-white hover:bg-[var(--bg-2)]"}`}
              data-testid={`asset-view-${v.k}`}>
              <v.icon size={13} /> {v.l}
            </button>
          ))}
        </div>

        {(view === "board" || view === "register") && (
          <div className="inline-flex flex-wrap gap-1.5" data-testid="asset-type-filter">
            <button onClick={() => setTypeFilter("")}
              className={`text-[11px] px-2.5 py-1 rounded border transition-colors ${typeFilter === "" ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--line)] hover:border-[var(--accent)]"}`}
              data-testid="asset-filter-all">All types</button>
            {ASSET_TYPES.map((t) => (
              <button key={t.key} onClick={() => setTypeFilter(t.key)}
                className={`text-[11px] px-2.5 py-1 rounded border inline-flex items-center gap-1 transition-colors ${typeFilter === t.key ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "border-[var(--line)] hover:border-[var(--accent)]"}`}
                data-testid={`asset-filter-${t.key}`}>
                <t.icon size={12} /> {t.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {view === "register" && selected.size > 0 && (
        <div className="mb-3 flex items-center gap-3 flex-wrap px-4 py-2.5 rounded-lg border border-[var(--accent)] bg-[var(--accent-soft,#DDF3F1)]" data-testid="asset-bulk-bar">
          <span className="text-sm font-semibold" data-testid="asset-bulk-count">{selected.size} selected{selected.size === assets.length && assets.length > 0 ? " (all filtered assets, across every page)" : ""}</span>
          <button onClick={() => bulkDocRef.current?.click()} disabled={bulkBusy} className="btn-ghost text-xs" data-testid="asset-bulk-attach">
            <Paperclip size={13} /> {bulkBusy ? "Working…" : "Attach warranty certificate"}
          </button>
          <button onClick={bulkRemoveDoc} disabled={bulkBusy} className="btn-ghost text-xs text-[#C0392B]" data-testid="asset-bulk-remove">
            <XCircle size={13} /> Remove certificate
          </button>
          <input ref={bulkDocRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" className="hidden" onChange={bulkAttach} data-testid="asset-bulk-input" />
          <button onClick={() => setSelected(new Set())} className="text-xs muted hover:text-[var(--ink)] ml-auto" data-testid="asset-bulk-clear">Clear selection</button>
        </div>
      )}

      {loading ? (
        <div className="muted text-sm">Loading assets…</div>
      ) : view === "board" ? (
        <Board assets={assets} onReclass={reclass} onEdit={(a) => { setEditing(a); setModalOpen(true); }} />
      ) : view === "register" ? (
        <Register assets={assets} onEdit={(a) => { setEditing(a); setModalOpen(true); }} onDelete={remove}
          selected={selected} onToggle={toggleSel} onToggleAll={toggleAll}
          projects={projects} onAssign={assignSite} />
      ) : view === "report" ? (
        <Report report={report} />
      ) : view === "claims" ? (
        <ClaimsDashboard data={claimsData} />
      ) : (
        <WarrantyValue data={valueData} />
      )}

      <AssetModal open={modalOpen} onClose={() => setModalOpen(false)} onSaved={load}
        projects={projects} existing={editing} />
    </div>
  );
}
